import React, { useState, useRef, useEffect, useContext } from 'react';
import styled from 'styled-components';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import ProfileMenu from '../common/ProfileMenu';
import { popularCompanies } from '../../data/popularCompanies';
import { AuthContext } from '../../contexts/AuthContext';
import supabase from '../../config/supabaseClient';

const HeaderContainer = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  background: white;
  border-bottom: 1px solid var(--border);
`;

const Logo = styled(NavLink)`
  font-size: 24px;
  font-weight: bold;
  color: #000;
  text-decoration: none;
`;

const Navigation = styled.nav`
  display: flex;
  align-items: center;
  gap: 24px;
  margin-left: 48px;
`;

const NavItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  color: #666;
  text-decoration: none;
  font-weight: 500;
  position: relative;
  cursor: pointer;

  &:hover {
    color: #333;
  }

  ${props => props.active && `
    color: #000;
  `}
`;

const Badge = styled.span`
  background: #E5F0FF;
  color: #2563eb;
  padding: 2px 6px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
`;

const PopularCompaniesDropdown = styled.div`
  position: absolute;
  top: calc(100% + 12px);
  left: 0;
  background: white;
  border: 1px solid #eee;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  width: 300px;
  z-index: 1000;
  padding: 8px 0;
`;

const DropdownHeader = styled.div`
  padding: 16px;
  font-weight: 600;
  color: #666;
  border-bottom: 1px solid #eee;
`;

const CompanyList = styled.div`
  max-height: 400px;
  overflow-y: auto;
`;

const CompanyItem = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 16px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #f8f9fa;
  }
`;

const CompanyLogo = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  object-fit: contain;
`;

const CompanyInfo = styled.div`
  flex: 1;
`;

const CompanyName = styled.div`
  font-weight: 500;
  font-size: 16px;
  color: #333;
`;

const CompanyCategory = styled.div`
  font-size: 13px;
  color: #666;
`;

const NavItemWithDropdown = styled.div`
  position: relative;
`;

const Icon = styled.span`
  margin-right: 8px;
`;

const DashboardHeader = () => {
  const [showCompanies, setShowCompanies] = useState(false);
  const [subscriptionCount, setSubscriptionCount] = useState(0);
  const [paymentMethodCount, setPaymentMethodCount] = useState(0);
  const [tagCount, setTagCount] = useState(0);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useContext(AuthContext);
  const [subscriptions, setSubscriptions] = useState([]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowCompanies(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!user) return;

    const loadSubscriptions = async () => {
      try {
        const { data, error } = await supabase
          .from('subscriptions')
          .select('*')
          .eq('user_id', user.id)
          .eq('status', 'active');

        if (error) throw error;
        setSubscriptions(data || []);
      } catch (error) {
        console.error('Error loading subscriptions:', error);
      }
    };

    loadSubscriptions();

    // Subscribe to real-time changes
    const subscription = supabase
      .channel('subscription_changes')
      .on('postgres_changes', 
        {
          event: '*',
          schema: 'public',
          table: 'subscriptions',
          filter: `user_id=eq.${user.id}`
        }, 
        (payload) => {
          loadSubscriptions();
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [user]);

  useEffect(() => {
    const loadData = async () => {
      if (!user) return;

      try {
        // Get subscriptions count
        const { data: subscriptions, error: subError } = await supabase
          .from('subscriptions')
          .select('*')
          .eq('user_id', user.id);

        if (subError) throw subError;
        setSubscriptionCount(subscriptions?.length || 0);

        // Get payment methods count
        const { data: paymentMethods, error: pmError } = await supabase
          .from('payment_methods')
          .select('*')
          .eq('user_id', user.id);

        if (pmError) throw pmError;
        setPaymentMethodCount(paymentMethods?.length || 0);

        // Get tags count
        const { data: tags, error: tagError } = await supabase
          .from('tags')
          .select('*')
          .eq('user_id', user.id);

        if (tagError) throw tagError;
        setTagCount(tags?.length || 0);

      } catch (error) {
        console.error('Error loading data:', error);
      }
    };

    loadData();
  }, [user]);

  const handleSubscriptionClick = (e) => {
    if (e.target === e.currentTarget) {
      // If clicking the main nav item, navigate to subscriptions
      navigate('/subscriptions');
    } else {
      // If clicking the dropdown area, just toggle the dropdown
      e.preventDefault();
      setShowCompanies(!showCompanies);
    }
  };

  const handleCompanyClick = (company) => {
    navigate('/subscriptions/create', { 
      state: { 
        company: company 
      }
    });
    setShowCompanies(false);
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  return (
    <>
      <HeaderContainer>
        <HeaderLeft>
          <Logo to="/dashboard">nexpend.</Logo>
          <Navigation>
            <NavItem 
              active={isActive('/dashboard')} 
              onClick={() => navigate('/dashboard')}
            >
              <Icon>🏠</Icon>
              Dashboard
            </NavItem>
            <NavItem 
              active={isActive('/calendar')} 
              onClick={() => navigate('/calendar')}
            >
              <Icon>📅</Icon>
              Calendar
            </NavItem>
            <NavItemWithDropdown ref={dropdownRef}>
              <NavItem 
                as={NavLink} 
                to="/subscriptions"
                onClick={handleSubscriptionClick}
              >
                <Icon>📦</Icon>
                Subscriptions
                <Badge>{subscriptionCount}</Badge>
              </NavItem>
              {showCompanies && (
                <PopularCompaniesDropdown>
                  <DropdownHeader>Popular Subscriptions</DropdownHeader>
                  <CompanyList>
                    {popularCompanies.map(company => (
                      <CompanyItem
                        key={company.id}
                        onClick={() => handleCompanyClick(company)}
                      >
                        <CompanyLogo 
                          src={company.logo} 
                          alt={company.name}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = '🏢';
                          }}
                        />
                        <CompanyInfo>
                          <CompanyName>{company.name}</CompanyName>
                          <CompanyCategory>{company.category}</CompanyCategory>
                        </CompanyInfo>
                      </CompanyItem>
                    ))}
                  </CompanyList>
                </PopularCompaniesDropdown>
              )}
            </NavItemWithDropdown>
            <NavItem 
              as={NavLink} 
              to="/payment-methods"
            >
              <Icon>💳</Icon>
              Payment Methods
              <Badge>{paymentMethodCount}</Badge>
            </NavItem>
            <NavItem 
              as={NavLink} 
              to="/tags"
            >
              <Icon>🏷️</Icon>
              Tags
              <Badge>{tagCount}</Badge>
            </NavItem>
          </Navigation>
        </HeaderLeft>
        <ProfileMenu />
      </HeaderContainer>
    </>
  );
};

export default DashboardHeader; 